import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import { listNotifications, markNotificationRead, markAllNotificationsRead } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}

export default function NotificationsBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = () => {
    if (!user) return;
    listNotifications().then((d) => {
      setItems(d.items || []);
      setUnread(d.unread || 0);
    }).catch(() => {});
  };

  useEffect(() => {
    if (!user) { setItems([]); setUnread(0); return; }
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user) return null;

  const onItemClick = async (item) => {
    if (!item.read) {
      try {
        await markNotificationRead(item.id);
        setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, read: true } : i)));
        setUnread((u) => Math.max(0, u - 1));
      } catch {}
    }
  };

  const onReadAll = async () => {
    try {
      await markAllNotificationsRead();
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
      setUnread(0);
    } catch {}
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notifications-bell"
        className="relative w-10 h-10 grid place-items-center border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] text-zinc-300 transition-colors cut-corners"
        aria-label="Notifications"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            data-testid="notifications-unread-dot"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D4AF37] text-black text-[10px] font-black grid place-items-center ring-2 ring-black"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-[#0A0A0A] border border-[#D4AF37]/30 cut-corners shadow-2xl z-50"
        >
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37]">Dispatch</span>
            {unread > 0 && (
              <button onClick={onReadAll} data-testid="mark-all-read"
                className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#D4AF37] flex items-center gap-1">
                <Check size={10} /> Mark all
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-center text-zinc-500 text-sm">All quiet on the throne.</div>
          ) : (
            <ul>
              {items.map((n) => {
                const Body = (
                  <div
                    onClick={() => onItemClick(n)}
                    data-testid={`notif-${n.id}`}
                    className={`px-4 py-3 border-b border-white/5 hover:bg-white/5 cursor-pointer ${n.read ? "opacity-60" : ""}`}
                  >
                    <div className="flex items-start gap-3">
                      {!n.read && <span className="mt-1 w-2 h-2 rounded-full bg-[#D4AF37] shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-sm uppercase truncate text-white">{n.title}</div>
                        <p className="text-xs text-zinc-400 line-clamp-2">{n.body}</p>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-1">{timeAgo(n.created_at)}</div>
                      </div>
                    </div>
                  </div>
                );
                return (
                  <li key={n.id}>
                    {n.link ? (
                      n.link.startsWith("/#") ? (
                        <a href={n.link} onClick={() => setOpen(false)}>{Body}</a>
                      ) : (
                        <Link to={n.link} onClick={() => setOpen(false)}>{Body}</Link>
                      )
                    ) : Body}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
